import { useMemo } from "react";
import { FormikValues, useFormikContext } from "formik";
import { FormikProps } from "formik/dist/types";
import { FormationContextType } from "../Providers/Formation";
import { useFormEditState } from "@/lib/Components/Form/Hooks/useFormEditState";

export type FormationContext<TValue> = FormikProps<TValue> &
  FormationContextType & {
    hasUnsavedChanges: boolean;
    errorCount: number;
  };

export function useFormState<
  TValue = FormikValues,
>(): FormationContext<TValue> {
  const context = useFormikContext<TValue>();
  const formationContext = useFormEditState();

  const hasUnsavedChanges = formationContext.isEditing && context.dirty;

  const errorCount = useMemo(
    () => countErrors(context.errors),
    [context.errors],
  );

  // Memoize the entire return object
  return useMemo(
    () => ({
      ...context,
      ...formationContext,
      hasUnsavedChanges,
      errorCount,
    }),
    [context, formationContext, hasUnsavedChanges, errorCount],
  );
}

function countErrors(errors: unknown): number {
  if (errors === null || errors === undefined) {
    return 0;
  }

  if (typeof errors !== "object") {
    return 1;
  }

  return Object.values(errors).reduce<number>(
    (count, value) => count + countErrors(value),
    0,
  );
}
